import React, { useState, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "../../../i18n/LanguageContext";
import {
  faTimes,
  faCheckCircle,
  faExclamationTriangle,
  faShieldAlt,
  faEnvelope,
  faGraduationCap,
  faChartPie,
  faExclamationCircle,
  faCog,
  faCheck,
  faBell,
} from "@fortawesome/free-solid-svg-icons";
import { formatTimeAgo as formatTimeAgoUtil } from "../utils/DateTimeUtil";
import "./css/NotificationItem.css";

/**
 * Decode HTML entities in text
 */
const decodeHtmlEntities = (text) => {
  if (!text) return text;
  const textarea = document.createElement("textarea");
  textarea.innerHTML = text;
  return textarea.value;
};

/**
 * Individual notification item component
 */
function NotificationItem({
  notification,
  onMarkAsRead,
  onDismiss,
  onAction,
  className = "",
  verificationFailed = false,
}) {
  const [isProcessing, setIsProcessing] = useState(false);
  const { t } = useTranslation();
  const itemRef = useRef(null);

  // When the delete/keep prompt appears (because the associated analysis was
  // deleted or could not be loaded), scroll this notification into view so the
  // user sees the option right here instead of having to scroll to find it.
  // `block: "nearest"` keeps the scope to the dropdown/mobile list container
  // and avoids jumping the whole page.
  useEffect(() => {
    if (verificationFailed && itemRef.current) {
      itemRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [verificationFailed]);

  // Use centralized DateTimeUtil for consistent time formatting
  // formatTimeAgo is imported from DateTimeUtil at the top of file
  const formatTimestamp = (timestamp) => {
    return formatTimeAgoUtil(timestamp);
  };

  const handleMarkAsRead = async () => {
    if (notification.isRead || isProcessing) return;

    setIsProcessing(true);
    try {
      await onMarkAsRead(notification.notificationId);
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDismiss = async () => {
    if (isProcessing) return;

    setIsProcessing(true);
    try {
      await onDismiss(notification.notificationId);
    } catch (error) {
      console.error("Failed to dismiss notification:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleActionClick = async () => {
    if (!notification.actionData || !onAction || isProcessing) return;

    setIsProcessing(true);
    try {
      const actionData =
        typeof notification.actionData === "string"
          ? JSON.parse(notification.actionData)
          : notification.actionData;

      console.log("NotificationItem: Action button clicked");
      console.log("NotificationItem: Raw actionData:", notification.actionData);
      console.log("NotificationItem: Parsed actionData:", actionData);

      // Mark as read when action is clicked
      if (!notification.isRead) {
        handleMarkAsRead();
      }

      await onAction(actionData, notification.notificationId);
    } catch (error) {
      console.error("Failed to parse action data:", error);
      console.error("Raw action data was:", notification.actionData);
    } finally {
      setIsProcessing(false);
    }
  };

  const getNotificationIcon = () => {
    switch (notification.notificationType) {
      case "EMAIL_ANALYSIS_COMPLETE":
        return faEnvelope;
      case "SCAM_DETECTED":
        return faExclamationTriangle;
      case "SAFE_EMAIL_CONFIRMED":
        return faCheckCircle;
      case "SYSTEM_ALERT":
        return faExclamationCircle;
      case "TRAINING_REMINDER":
        return faGraduationCap;
      case "SECURITY_UPDATE":
        return faShieldAlt;
      case "SIMULATION_RESULT":
        return faChartPie;
      case "ALERT_SHARED":
        return faBell;
      default:
        return faCog;
    }
  };

  const getPriorityClass = () => {
    switch (notification.priority) {
      case "CRITICAL":
        return "notification-critical";
      case "HIGH":
        return "notification-high";
      case "MEDIUM":
        return "notification-medium";
      case "LOW":
        return "notification-low";
      default:
        return "";
    }
  };

  const getActionData = () => {
    if (!notification.actionData) return null;

    try {
      return typeof notification.actionData === "string"
        ? JSON.parse(notification.actionData)
        : notification.actionData;
    } catch {
      return null;
    }
  };

  const actionData = getActionData();

  // Decode HTML entities in title and message
  const decodedTitle = decodeHtmlEntities(notification.title);
  const decodedMessage = decodeHtmlEntities(notification.message);

  return (
    <div
      ref={itemRef}
      className={`notification-item ${getPriorityClass()} ${
        !notification.isRead ? "unread" : ""
      } ${className}`}
    >
      <div className="notification-header">
        <div className="notification-icon">
          <FontAwesomeIcon icon={getNotificationIcon()} />
        </div>
        <div className="notification-content">
          <h4 className="notification-title">{decodedTitle}</h4>
          <div className="notification-meta">
            <span className="notification-time">
              {formatTimestamp(notification.createdAt)}
            </span>
            {notification.priority && notification.priority !== "MEDIUM" && (
              <span
                className={`notification-priority ${notification.priority.toLowerCase()}`}
              >
                {notification.priority}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="notification-body">
        <p className="notification-message">{decodedMessage}</p>

        {verificationFailed && (
          <div className="notification-verification-failed">
            <p className="notification-error-message">
              {t("notification.verificationFailed.message")}
            </p>
            <div className="notification-verification-actions">
              <button
                className="notification-delete-confirm-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDismiss();
                }}
                disabled={isProcessing}
              >
                {t("notification.verificationFailed.confirmDelete")}
              </button>
              <button
                className="notification-delete-cancel-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  // Just close - parent will handle clearing verification state
                  window.dispatchEvent(
                    new CustomEvent("cancel-notification-verification"),
                  );
                }}
                disabled={isProcessing}
              >
                {t("notification.verificationFailed.keepIt")}
              </button>
            </div>
          </div>
        )}

        <div className="notification-actions-container">
          {actionData && !verificationFailed && (
            <button
              className="notification-action-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleActionClick();
              }}
              disabled={isProcessing}
            >
              {actionData.buttonText || t("notification.viewDetails")}
            </button>
          )}

          <div className="notification-right-actions">
            {!notification.isRead && (
              <button
                className="notification-mark-read-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleMarkAsRead();
                }}
                disabled={isProcessing}
                title={t("notification.markAsRead")}
              >
                <FontAwesomeIcon icon={faCheck} />
              </button>
            )}
            <button
              className="notification-dismiss-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleDismiss();
              }}
              disabled={isProcessing}
              title={t("notification.dismiss")}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NotificationItem;
